import { useCallback, useState } from "react";

import { useBranchesData } from "~/branching/data-source/use-branches-data";
import type { Branch, BranchId } from "~/branching/model/branch";
import { resolveDefaultBranch } from "~/branching/model/resolve-default-branch";

type ReadyBase = {
  systemId: string;
  setSelectedBranch: (b: Branch | null) => void;
  selectedBranch: Branch | null;
  defaultBranchId: BranchId | null;
  branches: Branch[];
};

export type SystemBranchingResult =
  | ({ status: "loading" } & ReadyBase)
  | ({ status: "error"; error: Error; onRetry: () => void } & ReadyBase)
  | ({ status: "ready" } & ReadyBase);

export function useSystemBranching(options: {
  systemId: string | undefined;
  desiredBranchId?: BranchId | null;
  desiredBranchName?: string | null;
}): SystemBranchingResult {
  const { systemId, desiredBranchId = null, desiredBranchName = null } = options;
  const resolvedSystemId = systemId ?? "";
  const bq = useBranchesData(systemId, { enabled: !!systemId });
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);

  const setSelected = useCallback((b: Branch | null) => {
    setSelectedBranch(b);
  }, []);

  const emptyBranches: Branch[] = [];
  const branches = bq.data?.branches ?? emptyBranches;
  const branchStillExists = selectedBranch ? branches.find((branch) => branch.id === selectedBranch.id) : null;
  const derivedSelectedBranch =
    desiredBranchId !== null
      ? (branches.find((branch) => branch.id === desiredBranchId) ?? null)
      : desiredBranchName !== null
        ? (branches.find((branch) => branch.name === desiredBranchName) ?? null)
        : (branchStillExists ?? resolveDefaultBranch(branches));
  const baseReady: ReadyBase = {
    systemId: resolvedSystemId,
    setSelectedBranch: setSelected,
    selectedBranch: derivedSelectedBranch,
    defaultBranchId: bq.data?.defaultBranchId ?? null,
    branches,
  };

  if (bq.isError) {
    return {
      status: "error",
      error: bq.error instanceof Error ? bq.error : new Error("Failed to load branches"),
      onRetry: () => bq.refetch(),
      ...baseReady,
    };
  }

  if (!systemId || bq.isLoading || !bq.data) {
    return {
      status: "loading",
      systemId: resolvedSystemId,
      setSelectedBranch: setSelected,
      selectedBranch: null,
      defaultBranchId: null,
      branches: emptyBranches,
    };
  }

  return { status: "ready", ...baseReady };
}
